import { set as setValue } from 'dot-prop';
import {
  BreakingChangeType,
  Design,
  DesignModuleDeletedMap
} from '@app/types';
import { addBreakingChange, createUuid } from '@app/utilities';
import {
  DuplicateIdItem,
  DuplicateIdItemType
} from './get-duplicate-id-items';

interface AddDeletedItemProps {
  deletedItemId: string;
  deletedMapKey: keyof DesignModuleDeletedMap;
  design: Design;
  moduleId: string;
}

function addDeletedModuleItem({
  design,
  deletedItemId,
  deletedMapKey,
  moduleId
}: AddDeletedItemProps) {
  const deletedItems = design.deletedMap?.modules?.[moduleId]?.[deletedMapKey] ?? [];
  deletedItems.push(deletedItemId);
  setValue(
    design,
    `deletedMap.modules.${moduleId}.${deletedMapKey}`,
    deletedItems
  );
}

export interface FixDuplicateIdItemsProps {
  duplicateIdItems: DuplicateIdItem[];
  serialized: Design;
  timestamp: number;
}

/** Update items from the application with the same ID to have unique IDs. */
export function fixDuplicateIdItems({
  duplicateIdItems,
  serialized: design,
  timestamp
}: FixDuplicateIdItemsProps) {
  const { modules } = design.components;

  duplicateIdItems.forEach((item) => {
    switch (item.type) {
      case DuplicateIdItemType.node: {
        const { id: oldId, moduleId } = item;
        const newId = createUuid();
        const foundModule = modules.find((m) => {
          return m.id === moduleId;
        });

        if (!foundModule) {
          throw new Error(`Unable to find module (ID: ${moduleId}) that contains duplicate ID ${oldId}`);
        }

        const { links, nodes } = foundModule.versions[1].flow;

        nodes.forEach((node) => {
          if (node.id === oldId) {
            node.id = newId;
            node.props.lastLocalChange = timestamp;
            node.props.timeAddedToFlow = timestamp;
            addDeletedModuleItem({
              deletedItemId: oldId,
              deletedMapKey: 'nodes',
              moduleId,
              design
            });
          }
        });

        links.forEach((link) => {
          if (link.source === oldId) {
            link.source = newId;
            link.props.lastLocalChange = timestamp;
          }
          if (link.target === oldId) {
            link.target = newId;
            link.props.lastLocalChange = timestamp;
          }
        });
        break;
      }
      default:
        console.error(`Unknown duplicate ID item type '${item.type}'.`);
    }
  });

  addBreakingChange(design, {
    message: 'Duplicate IDs were fixed.',
    timestamp,
    type: BreakingChangeType.duplicateIdsFixed
  });
}
